// invoice details modal
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  OpenInNew as OpenInNewIcon,
  Link as LinkIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import PaymentHistory from './PaymentHistory';
import { formatCurrency, getInvoiceStatusColor, fetchPaymentModeFilters } from '../../utils/helper';
import api from '../../services/api';

const InvoiceDetails = ({ 
  open, 
  onClose, 
  invoice = null,
  showSuccessMessage = null,
  openPaymentDialogOnMount = false 
}) => {
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    reference: '',
    payment_method: 'CS'
  });
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [paymentModes, setPaymentModes] = useState([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(openPaymentDialogOnMount && open);

  // Initialize payment history from invoice
  React.useEffect(() => {
    if (invoice?.payment_history && Array.isArray(invoice.payment_history)) {
      setPaymentHistory(invoice.payment_history);
    } else {
      setPaymentHistory([]);
    }
  }, [invoice?.payment_history]);

  // Load payment modes from backend
  React.useEffect(() => {
    const loadPaymentModes = async () => {
      try {
        const modes = await fetchPaymentModeFilters();
        setPaymentModes(modes);
      } catch (error) {
        console.error('Error loading payment modes:', error);
        // Fallback to default if API fails
        setPaymentModes([
          { value: 'CS', label: 'Cash' },
          { value: 'CR', label: 'Credit Card' },
          { value: 'DB', label: 'Debit Card' },
          { value: 'BT', label: 'Bank Transfer' }
        ]);
      }
    };
    loadPaymentModes();
  }, []);

  // Auto-open payment dialog if requested
  React.useEffect(() => {
    if (open && openPaymentDialogOnMount && invoice) {
      setOpenPaymentDialog(true);
    } else if (!open) {
      setOpenPaymentDialog(false);
    }
  }, [open, openPaymentDialogOnMount, invoice]);

  // Reset payment form when dialog opens
  React.useEffect(() => {
    if (open) {
      setPaymentForm({
        amount: '',
        reference: '',
        payment_method: 'CS'
      });
      setShowValidationErrors(false);
    }
  }, [open]);

  if (!invoice) {
    return null;
  }

  // Safety check for required properties
  if (!invoice || !invoice.invoice_number) {
    console.warn('InvoiceDetails: Missing required invoice properties', invoice);
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Invoice Details</DialogTitle>
        <DialogContent>
          <Alert severity="error">
            Invoice data is incomplete. Please try refreshing the page.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'PD': return <CheckCircleIcon />;
      case 'UP': return <PaymentIcon />;
      case 'PP': return <WarningIcon />;
      case 'OV': return <WarningIcon />;
      case 'RP': return <WarningIcon />;
      case 'CN': return <WarningIcon />;
      default: return <ReceiptIcon />;
    }
  };

  const isOverdue = () => {
    if (!invoice.due_date) return false;
    return new Date(invoice.due_date) < new Date();
  };

  const getDaysUntilDue = () => {
    if (!invoice.due_date) return null;
    const dueDate = new Date(invoice.due_date);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDue = getDaysUntilDue();

  // Payment method options with icons
  const getPaymentIcon = (value) => {
    switch (value) {
      case 'CS': return <WalletIcon />;
      case 'CR': return <CreditCardIcon />;
      case 'DB': return <CreditCardIcon />;
      case 'BT': return <AccountBalanceIcon />;
      default: return <PaymentIcon />;
    }
  };

  const paymentMethods = paymentModes.map(mode => ({
    ...mode,
    icon: getPaymentIcon(mode.value)
  }));

  // Use total paid amount from backend
  const totalPaid = invoice.total_paid_amount || 0;
  const remainingAmount = (invoice.taxable_invoice_value || 0) - totalPaid;

  // Handle payment form changes
  const handlePaymentFormChange = (field, value) => {
    setPaymentForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle add payment
  const handleAddPayment = async () => {
    // Show validation errors when user tries to submit
    setShowValidationErrors(true);
    
    const amount = parseFloat(paymentForm.amount);
    
    // Validate all required fields
    if (!amount || amount <= 0) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid amount',
        severity: 'error'
      });
      return;
    }

    if (!paymentForm.reference || paymentForm.reference.trim() === '') {
      setSnackbar({
        open: true,
        message: 'Payment reference is required',
        severity: 'error'
      });
      return;
    }

    if (!paymentForm.payment_method) {
      setSnackbar({
        open: true,
        message: 'Please select a payment method',
        severity: 'error'
      });
      return;
    }

    if (amount > remainingAmount) {
      setSnackbar({
        open: true,
        message: `Payment amount cannot exceed remaining balance of ${formatCurrency(remainingAmount)}`,
        severity: 'error'
      });
      return;
    }

    try {
      // Prepare payload for backend API
      const paymentData = {
        mode: paymentForm.payment_method,
        reference: paymentForm.reference.trim(),
        amount: amount
      };

      // Call backend API
      const response = await api.post(`/invoice/${invoice.id}/payments/`, paymentData);

      if (response.data.success) {
        // Reset form and validation errors
        setPaymentForm({
          amount: '',
          reference: '',
          payment_method: 'CS'
        });
        setShowValidationErrors(false);
        setOpenPaymentDialog(false);

        setSnackbar({
          open: true,
          message: 'Payment processed successfully',
          severity: 'success'
        });

        if (showSuccessMessage) {
          showSuccessMessage('Payment processed successfully');
        }

        // Close the dialog and let parent component handle data refresh
        onClose();
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || 'Failed to process payment',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      
      let errorMessage = 'Error processing payment';
      
      if (error.response?.data?.details) {
        if (typeof error.response.data.details === 'object') {
          const firstError = Object.values(error.response.data.details)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        } else {
          errorMessage = error.response.data.details;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    }
  };


  // Invoice items data from backend - use exact field names
  const invoiceItems = (invoice.particulars || []).map((item) => ({
    id: item.id,
    description: item.description,
    quantity: item.quantity,
    unit_price: parseFloat(item.unit_price || 0),
    total_amount: parseFloat(item.total_amount || 0)
  }));

  // Calculate subtotal from actual line items total_amount
  const subtotal = invoiceItems.reduce((sum, item) => sum + item.total_amount, 0);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <ReceiptIcon />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Invoice Details
              </Typography>
            </Box>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 2 }}>
          {/* Invoice Header */}
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mr: 2 }}>
                      Invoice #{invoice.invoice_number}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(invoice.status)}
                      label={invoice.status_verbose || invoice.status}
                      color={getInvoiceStatusColor(invoice.status)}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {formatCurrency(invoice.taxable_invoice_value || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created on {formatDate(invoice.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Avatar sx={{ 
                      bgcolor: invoice.status === 'PD' 
                        ? 'success.main' 
                        : isOverdue() 
                          ? 'error.main' 
                          : 'warning.main', 
                      mr: 2 
                    }}>
                      <CalendarIcon />
                    </Avatar>
                    <Box sx={{ textAlign: 'right' }}>
                      {invoice.status === 'PD' ? (
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                          Paid on {formatDate(invoice.updated_at)}
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: isOverdue() ? 'error.main' : 'warning.main' }}>
                            Due {formatDate(invoice.due_date)}
                          </Typography>
                          {daysUntilDue !== null && (
                            <Typography variant="body2" color={isOverdue() ? 'error.main' : 'text.secondary'}>
                              {isOverdue() 
                                ? `${Math.abs(daysUntilDue)} days overdue`
                                : daysUntilDue === 0 
                                  ? 'Due today'
                                  : `${daysUntilDue} days remaining`
                              }
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Payment link (Razorpay) – small card with copy */}
          {invoice.payment_link && invoice.status !== 'PD' && invoice.status !== 'CN' && (
            <Paper
              variant="outlined"
              sx={{
                mb: 2,
                py: 1,
                px: 1.5,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                borderRadius: 1.5,
                bgcolor: 'grey.50',
              }}
            >
              <LinkIcon sx={{ color: 'primary.main', fontSize: 18 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Payment link
              </Typography>
              {invoice.payment_link_expires_at && (
                <Typography variant="caption" color="text.secondary">
                  · Expires {formatDate(invoice.payment_link_expires_at)}
                </Typography>
              )}
              <Tooltip title="Copy link">
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(invoice.payment_link);
                    setSnackbar({ open: true, message: 'Payment link copied', severity: 'success' });
                  }}
                  sx={{ ml: 0.5, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Paper>
          )}

          <Grid container spacing={3} sx={{ display: 'flex', flexDirection: 'column' }}>
            {/* Invoice Items */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Invoice Items ({invoiceItems.length})
                    </Typography>
                  </Box>
                  
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'primary.50' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }} align="center">Qty</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }} align="right">Unit Price</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '1rem' }} align="right">Total Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoiceItems.map((item) => (
                          <TableRow key={item.id} hover sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                            <TableCell>
                              <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                                {item.description}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                {item.quantity}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontSize: '1rem' }}>
                                {formatCurrency(item.unit_price)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontSize: '1rem', fontWeight: 700, color: 'primary.main' }}>
                                {formatCurrency(item.total_amount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        
                        {/* Subtotal Row */}
                        <TableRow sx={{ borderTop: '2px solid #e0e0e0' }}>
                          <TableCell colSpan={3}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              Subtotal
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {formatCurrency(subtotal)}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Discount Percentage Row - Always show (invoice-level discount only) */}
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Discount
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {(() => {
                                const pct = invoice.discount_percentage;
                                if (pct == null || pct === '') return '0%';
                                const num = typeof pct === 'string' ? parseFloat(String(pct).replace(/%/g, '')) : Number(pct);
                                return Number.isFinite(num) ? `${num.toFixed(1)}%` : String(pct);
                              })()}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Discount Amount Row - Always show (invoice-level discount only) */}
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              Discount Amount
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body1" sx={{ fontWeight: 600, color: invoice.discount_amount ? 'success.main' : 'text.primary' }}>
                              {invoice.discount_amount != null && invoice.discount_amount !== '' ? (typeof invoice.discount_amount === 'number' ? formatCurrency(invoice.discount_amount) : String(invoice.discount_amount)) : '₹0.00'}
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Tax Row */}
                        {parseFloat(invoice.tax_amount || 0) > 0 && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                GST (18%)
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {formatCurrency(parseFloat(invoice.tax_amount))}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Final Total Row */}
                        <TableRow sx={{ borderTop: '2px solid #1976d2', bgcolor: 'primary.50' }}>
                          <TableCell colSpan={3}>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                              Total Amount
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.dark' }}>
                              {formatCurrency(parseFloat(invoice.taxable_invoice_value || 0))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            {/* Payment History */}
            <Grid item xs={12} sx={{ width: '100%' }}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                      <PaymentIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Payment History ({paymentHistory.length})
                    </Typography>
                  </Box>

                  {/* Payment Summary */}
                  <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Total Invoice Amount:</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formatCurrency(invoice.taxable_invoice_value || 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Total Paid:</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main' }}>
                          {formatCurrency(totalPaid)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2" color="text.secondary">Remaining Balance:</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: remainingAmount > 0 ? 'error.main' : 'success.main' }}>
                          {formatCurrency(remainingAmount)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Add Payment Button */}
                  {remainingAmount > 0 && invoice?.shared_on && invoice?.status !== 'CN' && invoice?.status !== 'RP' && invoice?.status !== 'PD' && (
                    <Box sx={{ mb: 3, textAlign: 'center' }}>
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpenPaymentDialog(true)}
                        sx={{ borderRadius: 2, px: 3, py: 1 }}
                      >
                        Add Payment
                      </Button>
                    </Box>
                  )}

                  {/* Payment History Table */}
                  <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Payment Method</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date & Time</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentHistory.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Avatar sx={{ bgcolor: 'grey.400', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                                  <PaymentIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                  No Payment History
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  No payments have been made for this invoice yet.
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          paymentHistory.map((payment) => (
                            <TableRow key={payment.id} hover>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar 
                                    sx={{ 
                                      bgcolor: 'primary.main', 
                                      mr: 2, 
                                      width: 32, 
                                      height: 32,
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    {paymentMethods.find(m => m.value === payment.payment_mode)?.icon || <PaymentIcon />}
                                  </Avatar>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {payment.payment_mode_verbose}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                  {formatCurrency(payment.amount)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={payment.status_verbose}
                                  color={payment.status === 'PS' ? 'success' : 'warning'}
                                  size="small"
                                  icon={payment.status === 'PS' ? <CheckCircleIcon /> : <WarningIcon />}
                                  sx={{ 
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    height: 24
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontFamily: 'monospace', 
                                    fontWeight: 500,
                                    fontSize: '0.75rem',
                                    color: 'text.secondary'
                                  }}
                                >
                                  {payment.payment_reference}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                  {payment.payment_date || new Date(payment.created_at).toLocaleString()}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          {/* <Button onClick={onClose} color="inherit">
            Close
          </Button> */}
          {/* <Button 
            variant="outlined" 
            startIcon={<DownloadIcon />} 
            sx={{ borderRadius: 2 }}
            onClick={() => {
              const link = document.createElement('a');
              link.href = invoice.invoice_url;
              link.download = `${invoice.invoice_number}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download PDF
          </Button> */}
        </DialogActions>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog 
        open={openPaymentDialog} 
        onClose={() => {
          setOpenPaymentDialog(false);
          setShowValidationErrors(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon />
            Add New Payment
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={paymentForm.amount}
                  onChange={(e) => handlePaymentFormChange('amount', e.target.value)}
                  inputProps={{ min: 0, step: 0.01, max: remainingAmount }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  required
                  error={showValidationErrors && (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0)}
                  helperText={showValidationErrors && (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) ? 'Amount is required' : `Maximum: ${formatCurrency(remainingAmount)}`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Reference"
                  value={paymentForm.reference}
                  onChange={(e) => handlePaymentFormChange('reference', e.target.value)}
                  placeholder="Transaction ID, Check Number, etc."
                  required
                  error={showValidationErrors && (!paymentForm.reference || paymentForm.reference.trim() === '')}
                  helperText={showValidationErrors && (!paymentForm.reference || paymentForm.reference.trim() === '') ? 'Reference is required' : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth error={showValidationErrors && !paymentForm.payment_method} required>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    value={paymentForm.payment_method}
                    label="Payment Method"
                    onChange={(e) => handlePaymentFormChange('payment_method', e.target.value)}
                  >
                    {paymentMethods.map((method) => (
                      <MenuItem key={method.value} value={method.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {method.icon}
                          {method.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {showValidationErrors && !paymentForm.payment_method && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5, fontSize: '0.75rem' }}>
                      Payment method is required
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => {
              setOpenPaymentDialog(false);
              setShowValidationErrors(false);
            }}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPayment}
            disabled={
              !paymentForm.amount || 
              parseFloat(paymentForm.amount) <= 0 ||
              !paymentForm.reference ||
              paymentForm.reference.trim() === '' ||
              !paymentForm.payment_method ||
              !invoice?.shared_on
            }
            startIcon={<AddIcon />}
          >
            Add Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InvoiceDetails; 